import { Icon } from '@/components/Atoms/Icon/Icon';
import useTranslation from '@/hooks/useTranslation';
import classNames from '@/utils/classNames';
import CheckCircleIcon from '@bloomberg/web-icons-react/CheckCircle';
import ExclamationDiamondIcon from '@bloomberg/web-icons-react/ExclamationDiamond';
import ExclamationTriangleIcon from '@bloomberg/web-icons-react/ExclamationTriangle';
import InfoCircleIcon from '@bloomberg/web-icons-react/InfoCircle';
import XIcon from '@bloomberg/web-icons-react/X';
import type { HTMLAttributes, ReactNode } from 'react';
import type { FCC } from 'react-fc';
import styles from './Notification.module.css';

const stateIcons = {
    warning: <ExclamationTriangleIcon />,
    error: <ExclamationDiamondIcon />,
    success: <CheckCircleIcon />,
    information: <InfoCircleIcon />,
} as const;

/**
 * A basic notification message.
 */
export const Notification: FCC<
    {
        state?: 'success' | 'error' | 'warning' | 'information';
        asText?: boolean;
        icon?: ReactNode;
        fullWidth?: boolean;
        onClose?: () => void;
    } & HTMLAttributes<HTMLParagraphElement>
> = ({ children, state = 'warning', asText = false, icon, fullWidth = false, onClose, className, ...rest }) => {
    const { t } = useTranslation('common');
    return (
        <p
            className={classNames(
                styles.notification,
                className,
                'cds-t-label cds-t-label--b',
                asText ? 'cds-state__text' : 'cds-state-panel',
                fullWidth ? styles.fullWidth : ''
            )}
            data-cds-state={state}
            {...rest}
        >
            <span className={styles.content}>
                {icon ?? <Icon className={classNames('cds-state__icon', styles.icon)} use={stateIcons[state]} />}
                <span className={styles.text}>{children}</span>
                {onClose && (
                    <button
                        type="button"
                        aria-label={t('Actions.close_modal')}
                        className={classNames('cds-button--tertiary', 'cds-button--icon', styles.dismiss)}
                        onClick={onClose}
                    >
                        <XIcon />
                    </button>
                )}
            </span>
        </p>
    );
};
